import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Chip, Button, IconButton,
  Tooltip, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Paper, Avatar, Dialog, DialogContent, DialogActions,
  TextField, Tabs, Tab, CircularProgress, Alert, Snackbar, InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircle as ApproveIcon, Cancel as RejectIcon,
  Refresh as RefreshIcon, HourglassEmpty as PendingIcon,
  TaskAlt as DoneIcon, FactCheck as ApprovalsIcon,
  Search as SearchIcon, Close as CloseIcon,
  WarningAmber as WarnIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import ModuleHeader from '../../components/ModuleHeader';
import swal from '../../utils/swal';

// ──────────────────────────────────────────────────────────────────
// Shared styling — same PRO_TABLE used across the system.
// ──────────────────────────────────────────────────────────────────
const PRO_TABLE = {
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 800, fontSize: 10.5, letterSpacing: 0.6,
    textTransform: 'uppercase', color: 'text.secondary',
    bgcolor: '#f7f9fc', py: 1, borderBottom: '1px solid #d9dee6',
  },
  '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)':
    { backgroundColor: '#fafbfc' },
  '& .MuiTableBody-root .MuiTableRow-root:hover':
    { backgroundColor: 'rgba(25,118,210,0.06) !important' },
  '& .MuiTableBody-root .MuiTableCell-root': {
    fontSize: 12.5, py: 1.05, borderBottom: '1px solid #eef0f4',
  },
};

const initials = (s) => (s || '').split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
const dateFmt  = (s) => s ? new Date(s).toLocaleString('en-GH',
  { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const STATUSES = [
  { key: 'PENDING',  label: 'Pending',  tone: 'warning', icon: <PendingIcon /> },
  { key: 'APPROVED', label: 'Approved', tone: 'success', icon: <DoneIcon /> },
  { key: 'REJECTED', label: 'Rejected', tone: 'error',   icon: <RejectIcon /> },
];

function StatusChip({ status }) {
  const s = STATUSES.find(x => x.key === status);
  if (!s) return <Chip size="small" label={status} />;
  return (
    <Chip size="small" color={s.tone} label={s.label}
      sx={{ fontWeight: 700, fontSize: 10.5, height: 20 }} />
  );
}

// ──────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────
export default function HRApprovalsPage() {
  const [tab, setTab]       = useState('PENDING');
  const [rows, setRows]     = useState([]);
  const [loading, setL]     = useState(true);
  const [error, setErr]     = useState('');
  const [q, setQ]           = useState('');
  const [toast, setToast]   = useState(null);

  // Totals across all statuses — drives the hero strip + tab badges.
  const [counts, setCounts] = useState({ PENDING: 0, APPROVED: 0, REJECTED: 0 });

  // Reject dialog state
  const [rejOpen, setRejOpen]     = useState(false);
  const [rejTarget, setRejTarget] = useState(null);
  const [rejReason, setRejReason] = useState('');
  const [rejBusy, setRejBusy]     = useState(false);
  const [rejErr,  setRejErr]      = useState('');

  const loadRows = useCallback(async () => {
    setL(true); setErr('');
    try {
      const r = await API.get('/hr/employee-delete-requests', { params: { status: tab } });
      setRows(r.data || []);
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not load requests');
    } finally { setL(false); }
  }, [tab]);

  // Lightweight count fetch — one request per status. Pulls full lists
  // because the API returns rows (no count-only endpoint exists), but
  // these are tiny so the cost is fine. Triggered on mount and after
  // every approve/reject.
  const loadCounts = useCallback(async () => {
    try {
      const res = await Promise.all(STATUSES.map(s =>
        API.get('/hr/employee-delete-requests', { params: { status: s.key } })
          .then(r => [s.key, (r.data || []).length])
          .catch(() => [s.key, 0])
      ));
      setCounts(Object.fromEntries(res));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  // Filter (does not affect counts, only what we render in the table).
  const visible = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.employee_name || '').toLowerCase().includes(t) ||
      (r.employee_code || '').toLowerCase().includes(t) ||
      (r.reason || '').toLowerCase().includes(t) ||
      (r.requested_by || '').toLowerCase().includes(t)
    );
  }, [rows, q]);

  const refreshAll = async () => {
    await Promise.all([loadRows(), loadCounts()]);
    window.dispatchEvent(new Event('pendingCountsChanged'));
  };

  const approve = async (row) => {
    const ok = await swal.confirm({
      title: 'Approve deletion',
      text: `Approve deletion of ${row.employee_name}? They will be marked inactive immediately. Payroll history is preserved.`,
      tone: 'success',
    });
    if (!ok) return;
    try {
      await API.post(`/hr/employee-delete-requests/${row.id}/approve`);
      setToast({ msg: `${row.employee_name} deactivated`, severity: 'success' });
      await refreshAll();
    } catch (e) {
      setToast({ msg: e.response?.data?.message || 'Approve failed', severity: 'error' });
    }
  };

  const openReject = (row) => {
    setRejTarget(row); setRejReason(''); setRejErr(''); setRejOpen(true);
  };
  const reject = async () => {
    if (rejReason.trim().length < 5) { setRejErr('Reason must be at least 5 characters'); return; }
    setRejBusy(true); setRejErr('');
    try {
      await API.post(`/hr/employee-delete-requests/${rejTarget.id}/reject`, { reason: rejReason.trim() });
      setRejOpen(false);
      setToast({ msg: 'Request rejected', severity: 'success' });
      await refreshAll();
    } catch (e) {
      setRejErr(e.response?.data?.message || 'Reject failed');
    } finally { setRejBusy(false); }
  };

  return (
    <Box sx={{ px: { xs: 1, md: 1.5 }, py: 0.5 }}>
      <ModuleHeader
        icon={<ApprovalsIcon />}
        title="HR Approvals"
        subtitle="Review and act on pending HR requests — currently employee deletions"
      />

      {/* ── Hero stats strip ───────────────────────────────────── */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} sx={{ mb: 2 }}>
        {STATUSES.map(s => (
          <HeroTile key={s.key} icon={s.icon} tone={s.tone}
            label={`${s.label} requests`} value={counts[s.key] ?? 0}
            active={tab === s.key}
            onClick={() => setTab(s.key)} />
        ))}
      </Stack>

      <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        {/* Tabs with badge counts — also act as filters in sync with the strip */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          variant="scrollable" scrollButtons="auto"
          sx={{ px: 2, borderBottom: 1, borderColor: 'divider', bgcolor: '#fafbfc' }}>
          {STATUSES.map(s => (
            <Tab key={s.key} value={s.key}
              iconPosition="start"
              icon={s.icon}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{s.label}</span>
                  <Chip size="small" label={counts[s.key] ?? 0}
                    color={tab === s.key ? s.tone : 'default'}
                    sx={{ height: 18, fontSize: 10.5, fontWeight: 800 }} />
                </Stack>
              } />
          ))}
        </Tabs>

        {/* Toolbar — search + reload */}
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
          borderBottom: 1, borderColor: 'divider' }}>
          <TextField size="small" placeholder="Search by employee, code, reason, requester…"
            value={q} onChange={(e) => setQ(e.target.value)}
            sx={{ flex: 1, maxWidth: 460 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: q ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQ('')}><CloseIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null,
            }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.6 }}>
            {visible.length} of {rows.length} shown
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Reload">
            <span><IconButton onClick={refreshAll} disabled={loading}><RefreshIcon /></IconButton></span>
          </Tooltip>
        </Box>

        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {/* Table */}
        <TableContainer>
          <Table size="small" sx={PRO_TABLE}>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Requested by</TableCell>
                <TableCell>When</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right" sx={{ width: 200 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={22} />
                </TableCell></TableRow>
              )}
              {!loading && visible.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center"
                  sx={{ color: 'text.disabled', fontStyle: 'italic', py: 5 }}>
                  {q
                    ? `No ${tab.toLowerCase()} requests match "${q}".`
                    : `No ${tab.toLowerCase()} requests.`}
                </TableCell></TableRow>
              )}
              {!loading && visible.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.2}>
                      <Avatar sx={{
                        width: 34, height: 34, fontSize: 12, fontWeight: 800,
                        bgcolor: r.status === 'APPROVED' ? 'grey.400'
                               : r.status === 'REJECTED' ? 'error.light'
                               : 'primary.main',
                      }}>
                        {initials(r.employee_name) || '?'}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.2 }} noWrap>
                          {r.employee_name}
                        </Typography>
                        {r.employee_code && (
                          <Typography variant="caption" color="text.secondary"
                            sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                            {r.employee_code}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                      {r.reason}
                    </Typography>
                    {r.rejection_reason && (
                      <Box sx={{ mt: 0.6, p: 0.8, borderRadius: 1,
                        bgcolor: (t) => alpha(t.palette.error.main, 0.06),
                        borderLeft: 3, borderColor: 'error.main' }}>
                        <Typography variant="caption" color="error.dark"
                          sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Rejection note
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'error.dark' }}>
                          {r.rejection_reason}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{r.requested_by || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                    {dateFmt(r.requested_at)}
                    {r.approved_at && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 700 }}>
                        ✓ {dateFmt(r.approved_at)}
                      </Typography>
                    )}
                    {r.rejected_at && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'error.main', fontWeight: 700 }}>
                        ✗ {dateFmt(r.rejected_at)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center"><StatusChip status={r.status} /></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {r.status === 'PENDING' ? (
                      <Stack direction="row" spacing={0.6} justifyContent="flex-end">
                        <Button size="small" color="success" variant="contained"
                          startIcon={<ApproveIcon />} onClick={() => approve(r)}
                          sx={{ textTransform: 'none', fontWeight: 800 }}>
                          Approve
                        </Button>
                        <Button size="small" color="error" variant="outlined"
                          startIcon={<RejectIcon />} onClick={() => openReject(r)}
                          sx={{ textTransform: 'none', fontWeight: 800 }}>
                          Reject
                        </Button>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        by <b>{r.status === 'APPROVED' ? r.approved_by : r.rejected_by}</b>
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* ── Reject dialog — polished gradient header ─────────── */}
      <Dialog open={rejOpen} onClose={() => setRejOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <Box sx={{
          px: 3, py: 2,
          background: (t) => `linear-gradient(135deg, ${t.palette.error.main} 0%, ${t.palette.error.dark} 100%)`,
          color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Avatar sx={{
            width: 44, height: 44, bgcolor: 'rgba(255,255,255,0.22)',
            border: '1.5px solid rgba(255,255,255,0.3)',
          }}>
            <WarnIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.2 }} noWrap>
              Reject delete request
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {rejTarget?.employee_name} — they'll stay active
            </Typography>
          </Box>
          <IconButton onClick={() => setRejOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ bgcolor: '#fafbfc', p: 3 }}>
          {rejErr && <Alert severity="error" sx={{ mb: 2 }}>{rejErr}</Alert>}

          {/* Show the original request reason so the rejecter has context */}
          {rejTarget?.reason && (
            <Box sx={{ p: 1.5, mb: 2, borderRadius: 1.5,
              border: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}>
              <Typography variant="caption" sx={{ fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary', fontSize: 10 }}>
                Original request reason
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                {rejTarget.reason}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                — {rejTarget.requested_by || 'unknown'} on {dateFmt(rejTarget.requested_at)}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Tell the requester why this can't proceed. Your reason is saved
            with the request and visible from the Rejected tab.
          </Typography>
          <TextField fullWidth multiline rows={3} size="small" autoFocus
            label="Rejection reason *" value={rejReason}
            onChange={(e) => setRejReason(e.target.value)}
            placeholder="e.g. Employee still has unsettled fee balances · documentation missing" />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.8, bgcolor: 'background.paper',
          borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setRejOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" color="error" onClick={reject} disabled={rejBusy}
            size="large" startIcon={<RejectIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}>
            {rejBusy ? 'Rejecting…' : 'Reject request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.severity || 'info'} variant="filled" onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────
// HeroTile — clickable stat card that doubles as a status filter.
// Active card carries the tone colour as a fill; the others are
// neutral with a tinted icon + a thin colored left border.
// ──────────────────────────────────────────────────────────────────
function HeroTile({ icon, label, value, tone = 'primary', active, onClick }) {
  return (
    <Card
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
      sx={{
        flex: 1, minWidth: 0, borderRadius: 2,
        borderLeft: 4, borderColor: `${tone}.main`,
        boxShadow: active ? `0 4px 14px ${alpha('#000', 0.10)}` : '0 1px 3px rgba(0,0,0,0.06)',
        bgcolor: active ? (t) => alpha(t.palette[tone].main, 0.08) : 'background.paper',
        cursor: 'pointer',
        transition: 'all 0.15s',
        '&:hover': {
          boxShadow: `0 4px 12px ${alpha('#000', 0.10)}`,
          bgcolor: (t) => alpha(t.palette[tone].main, active ? 0.10 : 0.04),
        },
      }}
    >
      <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1.6, '&:last-child': { pb: 1.6 } }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: active ? `${tone}.main` : (t) => alpha(t.palette[tone].main, 0.12),
          color: active ? '#fff' : `${tone}.main`,
        }}>{icon}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.5,
                  textTransform: 'uppercase', fontSize: 10, lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1.2,
            color: active ? `${tone}.dark` : 'text.primary' }} noWrap>
            {value}
          </Typography>
        </Box>
        {active && (
          <Chip size="small" label="Showing" color={tone}
            sx={{ fontWeight: 800, fontSize: 9.5, height: 18 }} />
        )}
      </CardContent>
    </Card>
  );
}
